import AuthService from '../service/auth';
import { ROLES } from '../constnats/user';
import { REVIEW_STATUS } from '../constnats/review';
import { UPDATE_PHASE } from '../constnats/tableUpdate';

/**
 * Determines whether the current user sees the full review.
 * Every user can open any review; this only decides how much of it renders.
 * Returns true -> the full review (metadata, diffs, reconciliations, actions) is shown.
 * Returns false -> the user still opens the review but sees only a limited view:
 * a basic metadata header, the update description, and the review notes.
 * The full view of a DISCONTINUED review is reserved for admins, the update
 * owner, and QA users while the update is in the QA review phase; everyone else
 * gets the limited view. Once the update is applied there is nothing left to
 * gate, so the full view is shown to everyone. Any other review is always shown
 * in full.
 */
export function canViewFullReview(review: { status: string }, updatePhase: string, updateOwner: number | string): boolean {
    if (
        review.status !== REVIEW_STATUS.DISCONTINUED
        || updatePhase === UPDATE_PHASE.APPLIED
        || AuthService.hasAnyGlobalRole([ROLES.ADMIN])
        || AuthService.getUserId() === updateOwner
    ) {
        return true;
    }
    return AuthService.hasAnyGlobalRole([ROLES.QA]) && updatePhase === UPDATE_PHASE.READY_FOR_QA_REVIEW;
}

/**
 * Determines whether the current user can request a deep revision of the given review.
 * Deep revision only applies to automatic reviews; the backend rejects the request for any
 * other review, so this gate covers only the constraints the client can verify: the feature
 * is limited to beta testers, the update must not be applied, the review must have a branch
 * schema, and it must not already be approved.
 */
export function canRequestDeepRevision(review: { status: string, schemaId: number | null }, updatePhase: string): boolean {
    return AuthService.hasAnyGlobalRole([ROLES.BETA_FEATURE_TESTER_ROLE])
        && updatePhase !== UPDATE_PHASE.APPLIED
        && review.status !== REVIEW_STATUS.APPROVED
        && review.schemaId !== null;
}
